import fp from "lodash/fp";
import moment from "moment";
import { parsePhoneNumber } from "react-phone-number-input";

import {
	AddressCoordinatesConstants,
	AddressSectionFields,
} from "globalConstants";
import { getFullName } from "helpers";
import { isInactiveAgentStatus, processorsTypes } from "mappers";

/**
 * Checks if an object is empty.
 * @param {Object} obj - The object to check for emptiness.
 * @returns {boolean} - Returns true if the object is empty, otherwise false.
 */
export const isEmptyObject = obj => {
	return Object.keys(obj).length === 0;
};

/**
 * Checks if all fields in an object are empty. This includes nested objects and arrays.
 * @param {Object} obj - The object to check for empty fields.
 * @returns {boolean} - Returns true if all fields in the object are empty, otherwise false.
 */
export const areAllObjectFieldsEmpty = obj => {
	if (typeof obj !== "object" || obj === null) return false;
	if (Array.isArray(obj)) return obj.length === 0;

	return Object.values(obj).every(value => {
		if (typeof value === "object") {
			return areAllObjectFieldsEmpty(value);
		}
		return value === "" || value === null || value === undefined;
	});
};

/**
 * Checks if all fields in an object are empty.
 * @param {Object} obj - The object to check for empty fields.
 * @returns {boolean} - Returns true if all fields in the object are empty, otherwise false.
 */
export const areGeocodeFieldsEmpty = obj => {
	if (typeof obj !== "object" || obj === null) return false;
	if (Array.isArray(obj)) return obj.length === 0;

	for (let key in obj) {
		if (key === "search_address") continue;

		if (
			obj[key] !== null &&
			obj[key] !== 0 &&
			obj[key] !== "" &&
			obj[key] !== undefined
		) {
			return false;
		}
	}
	return true;
};

/**
 * Retrieves the value at a given path within a nested object.
 * @param {Object} obj - The object from which to retrieve the value.
 * @param {string} path - A string representing the path to the desired value (e.g., "ownerPrincipalSection.owners[0].address").
 * @returns {*} - The value at the specified path, or undefined if the path does not exist.
 */
export function getObjectByPath(obj, path) {
	return path?.split(".")?.reduce((acc, part) => {
		const match = part.match(/(\w+)\[(\d+)\]/);
		if (match) {
			return acc[match[1]][parseInt(match[2], 10)];
		}

		return acc && acc[part];
	}, obj);
}

/**
 * Checks if address coordinates correspond to manual entered
 * @param {Object} {lat, lng} - Pass latitude longitude.
 * @returns {boolean} isManual - Returns true if lat === "91" and/or lng === "181", otherwise false.
 */
export function checkIsLatOrLngManual({ lat, lng }) {
	return (
		lat?.toString() ===
			AddressCoordinatesConstants.ManualAddress.lat.toString() ||
		lng?.toString() ===
			AddressCoordinatesConstants.ManualAddress.lng.toString()
	);
}

/**
 * Checks if all address fields are empty
 * @param {Object} addressObj - Address object to check fields
 * @returns {boolean} - Returns true if all address fields is empty, otherwise false.
 */

export const checkAddressFieldsEmpty = addressObj =>
	![
		addressObj?.[AddressSectionFields.Street.name],
		addressObj?.[AddressSectionFields.City.name],
		addressObj?.[AddressSectionFields.Region.name],
		addressObj?.[AddressSectionFields.PostalCode.name],
		addressObj?.[AddressSectionFields.Country.name],
	].some(str => !!str);

/**
 * returns an array of a given object's property
 * takes { param1: value, obj1:{ nestedParam: value } }
 * returns ["param1", "obj1.nestedParam"]
 */
export const getNestedKeys = (object, path = "", keys = []) => {
	for (let key in object) {
		let rest = path.length ? "." + key : key;

		if (!fp.isEmpty(object[key]) && typeof object[key] === "object") {
			if (!Array.isArray(object[key])) {
				getNestedKeys(object[key], path + rest, keys);
			} else {
				object[key].map((obj, index) =>
					getNestedKeys(obj, `${path}${rest}[${index}]`, keys),
				);
			}
		} else keys.push(path + rest);
	}
	return keys;
};

export const generateAutotestValue = (autoTest, name) => {
	let autoTestValue;
	if (!autoTest) {
		autoTestValue = (name.charAt(0).toUpperCase() + name.slice(1)).replace(
			/\s/g,
			"",
		);
	} else {
		autoTestValue = autoTest;
	}
	return autoTestValue;
};

export const getAgentFullNameStatus = (option, withStatus = true) =>
	option &&
	`${getFullName(option)} ${
		isInactiveAgentStatus(option.status?.value) && withStatus
			? "(Inactive)"
			: ""
	}`;

export const removeEmptyStrings = obj => {
	let newObj = {};
	Object.keys(obj).forEach(prop => {
		if (obj[prop] !== "") {
			newObj[prop] = obj[prop];
		}
	});
	return newObj;
};

export const sleep = ms => {
	return new Promise(resolve => setTimeout(resolve, ms));
};

export const bytesToSize = bytes => {
	const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
	if (bytes === 0) return "0 Byte";
	const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
	return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
};

export const getProfitValue = (value, digits = 2) => {
	const sign = Math.sign(value);
	const fixedValue = value?.toFixed(digits);
	if (sign === 1) {
		return <span>+{fixedValue}</span>;
	} else if (sign === -1) {
		return <span className="text-danger">{fixedValue}</span>;
	} else if (sign === 0) {
		return <span>0</span>;
	} else {
		return <span>{fixedValue}</span>;
	}
};

export const checkRequestForAccessError = error =>
	error ? String(error).indexOf("403") !== -1 : false;

export const searchSelectOptions = (searchTerm, selectOptions) => {
	return selectOptions.filter(option => {
		return !!option.name.toLowerCase().match(searchTerm.toLowerCase());
	});
};

export const isDateValid = (dateFormat, data) => {
	if (!data || !dateFormat) {
		return false;
	}
	if (data.length !== 10) {
		return false;
	}
	const date = moment(data, dateFormat);
	if (date.isValid()) {
		return true;
	} else {
		return false;
	}
};

export const formatE164 = value => {
	if (!value) return "";

	return parsePhoneNumber(value)?.format("E.164");
};

// prettier-ignore
export const mapOptions =
	mutation =>
	(search, prevOptions, { page }) => {
		return mutation({ search, prevOptions, page })
	};

export const getProcessorSpecific = values => {
	return values.processorSpecific
		?.map(p => {
			if (p.processor === processorsTypes.TSYS) return { tsys: { ...p } };
			else if (p.processor === processorsTypes.FISERV_OMAHA)
				return { fiserv_omaha: { ...p } };
			else if (p.processor === processorsTypes.FISERV_NORTH)
				return { fiserv_north: { ...p } };
			else if (p.processor === processorsTypes.OTHER)
				return { other: { ...p } };
			return null;
		})
		.filter(p => p != null);
};

// Get indexes of duplicate values
// [2,3,3,4,5,2] => [0,1,2,5]
export const getDuplicatePosition = (arr, exception = "0") => {
	let result = [];
	let positions = {};
	// Collect all positions
	arr.forEach((value, pos) => {
		positions[value] = positions[value] || [];
		positions[value].push(pos);
	});
	// Check how much of same value in string
	Object.keys(positions).forEach(value => {
		if (value !== exception) {
			let posArray = positions[value];
			if (posArray.length > 1) {
				result = result.concat(posArray);
			}
		}
	});
	return result.sort();
};

export const checkIfKeyExist = (objectName, keyName) => {
	let keyExist = Object.keys(objectName).some(key => key === keyName);
	return keyExist;
};

/**
 * returns the first object that matches the property.
 * takes obj: { param1: value, obj1:{ nestedObj: {prop1: val} } }, propName: "nestedParam"
 * returns {prop1: val}
 */
export const findObjectWithProperty = (obj, propName) => {
	if (fp.has(propName)(obj)) {
		return obj;
	}
	if (fp.isArray(obj)) {
		for (const item of obj) {
			const found = findObjectWithProperty(item, propName);
			if (found) return found;
		}
	}
	if (fp.isObject(obj)) {
		for (const key in obj) {
			if (obj.hasOwnProperty(key)) {
				const found = findObjectWithProperty(obj[key], propName);
				if (found) return found;
			}
		}
	}
	return undefined;
};

export const safeParseFloat = (value, defaultValue) => {
	if (value === undefined || value === null) return defaultValue || undefined;
	return parseFloat(value.replaceAll(",", ""));
};

export const getStringWithoutHyphens = value => {
	if (!value) return null;

	return value.replaceAll("-", "");
};

export const encodeObjToURI = obj =>
	Object.keys(obj)
		.map(key => `${key}=${encodeURIComponent(obj[key])}`)
		.join("&");

export const getFirstCharacter = value => {
	if (value && typeof value === "string") {
		return Array.from?.(value)?.[0];
	} else {
		return "";
	}
};

export const capitalize = string =>
	typeof string === "string" && string
		? string[0].toUpperCase() + string.slice(1)
		: "";

// Format date to time 00:00:00
export const formatDateToTime = date => {
	if (!date) return "";

	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	const seconds = String(date.getSeconds()).padStart(2, "0");

	return `${hours}:${minutes}:${seconds}`;
};
